<app-card class="page-container">
  <app-card-content>
    <!-- Camera Selector - Multiple Cameras Available -->
    @if (showCameraSelector()) {
    <div class="flex flex-column flex-align-center text-center p-xl gap-lg" data-testid="camera-selector">
      <app-icon name="camera" [size]="48" />
      <h3>{{ 'scan.selectCamera.title' | translate }}</h3>
      <p>{{ 'scan.selectCamera.description' | translate }}</p>

      <div class="flex flex-column gap-sm camera-list">
        @for (camera of availableCameras(); track camera.deviceId) {
        <button class="camera-option" (click)="selectCamera(camera.deviceId)"
          [attr.data-testid]="'camera-option-' + $index">
          <app-icon name="camera" />
          <span>{{ camera.label || ('scan.selectCamera.cameraLabel' | translate:{ index: ($index + 1).toString() })
            }}</span>
          <app-icon name="chevron-right" />
        </button>
        }
      </div>
    </div>
    }

    <!-- Initial State - Before Scanning -->
    @if (!isScanning() && !scanResult() && !loading() && !showCameraSelector() && !nfcScanning()) {
    <div class="flex flex-column flex-align-center text-center p-xl gap-lg">
      <app-icon name="camera" [size]="48" />
      <h3>{{ 'scan.initial.title' | translate }}</h3>
      <p>{{ 'scan.initial.description' | translate }}</p>

      @if (cameraPermission() === 'denied') {
      <div class="flex flex-column flex-align-center gap-sm p-md my-lg permission-denied"
        data-testid="permission-denied">
        <app-icon name="alert-circle" />
        <p>{{ 'scan.permissionDenied' | translate }}</p>
      </div>
      }

      <app-button (click)="startScanning()" [disabled]="cameraPermission() === 'denied'"
        data-testid="start-scan-button">
        <app-icon name="camera" />
        {{ 'scan.startScanning' | translate }}
      </app-button>

      @if (nfcPlatform() === 'ios') {
      <span class="nfc-or">{{ 'scan.nfc.or' | translate }}</span>
      <app-button variant="outlined" (click)="readNfcTag()" data-testid="nfc-read-button">
        <app-icon name="smartphone-nfc" />
        {{ 'scan.nfc.tap' | translate }}
      </app-button>
      } @else if (nfcListening()) {
      <p class="flex flex-align-center gap-sm nfc-listening" data-testid="nfc-listening">
        <app-icon name="smartphone-nfc" />
        {{ 'scan.nfc.listening' | translate }}
      </p>
      } @else if (nfcDisabled()) {
      <div class="flex flex-column flex-align-center gap-sm nfc-disabled" data-testid="nfc-disabled">
        <p>{{ 'scan.nfc.disabledHint' | translate }}</p>
        <app-button variant="outlined" (click)="openNfcSettings()" data-testid="nfc-settings-button">
          <app-icon name="settings" />
          {{ 'scan.nfc.openSettings' | translate }}
        </app-button>
      </div>
      }
    </div>
    }

    <!-- NFC Scanning State (on iOS the system NFC sheet covers this) -->
    @if (nfcScanning()) {
    <div class="flex flex-column flex-align-center text-center p-xl gap-lg" data-testid="nfc-scanning">
      <app-icon name="smartphone-nfc" [size]="48" class="nfc-pulse" />
      <h3>{{ 'scan.nfc.scanning.title' | translate }}</h3>
      <p>{{ 'scan.nfc.scanning.instructions' | translate }}</p>
      <app-button variant="outlined" (click)="cancelNfc()" data-testid="nfc-cancel-button">
        <app-icon name="x" />
        {{ 'scan.cancel' | translate }}
      </app-button>
    </div>
    }

    <!-- Scanning State -->
    @if (isScanning()) {
    <div class="scan-active flex flex-column flex-align-center gap-lg p-md">
      <div class="video-container">
        <video #videoElement autoplay playsinline data-testid="video-element"></video>
        <div class="scan-overlay">
          <div class="scan-frame"></div>
        </div>
        <!-- Progress bar for camera timeout -->
        <div class="progress-bar-container" data-testid="progress-bar-container">
          <div class="progress-bar" [style.width.%]="progressPercent()" data-testid="progress-bar"></div>
          <div class="progress-text">{{ (remainingTimeMs() / 1000) | number:'1.0-0' }}s</div>
        </div>
      </div>

      <!-- container useful for rotated styles -->
      <div class="scan-instructions flex-column gap-md flex-align-center">
        <span>{{ 'scan.instructions' | translate }}</span>
        @if (nfcListening()) {
        <span class="flex flex-align-center gap-sm" data-testid="nfc-listening-camera">
          <app-icon name="smartphone-nfc" />
          {{ 'scan.nfc.listening' | translate }}
        </span>
        }
        @if (availableCameras().length > 1) {
        <button class="change-camera-link" (click)="changeCamera()" data-testid="change-camera-button">
          {{ 'scan.changeCamera' | translate }}
        </button>
        }

        <app-button variant="outlined" (click)="stopScanning()" data-testid="stop-scan-button">
          <app-icon name="x" />
          {{ 'scan.cancel' | translate }}
        </app-button>
      </div>
    </div>
    }

    <!-- Loading State -->
    @if (loading()) {
    <div class="flex flex-column flex-align-center gap-lg p-xl loading-container">
      <app-spinner />
      <p>{{ 'scan.loading' | translate }}</p>
    </div>
    }

    <!-- Error State -->
    @if (error() && !loading() && !isScanning()) {
    <div class="flex flex-column flex-align-center gap-lg p-xl text-center error-message" data-testid="error-message">
      <app-icon name="alert-circle" />
      <p>{{ error() }}</p>
      <app-button (click)="scanAnother()" data-testid="retry-button">
        <app-icon name="camera" />
        {{ 'scan.scanAgain' | translate }}
      </app-button>
    </div>
    }

    <!-- Success State - Asset Found -->
    @if (scanResult() && !loading()) {
    <div class="flex flex-column flex-align-center gap-lg p-xl" data-testid="scan-success">
      <div class="success-icon">
        <app-icon name="check-circle" [size]="48" />
      </div>

      <h3>{{ 'scan.assetFound.title' | translate }}</h3>

      <div class="p-lg checkPoint-details">
        <div class="checkPoint-field">
          <span class="field-label">{{ 'scan.assetFound.nameLabel' | translate }}</span>
          <span class="field-value">{{ scanResult()!.name }}</span>
        </div>

        @if (scanResult()!.description) {
        <div class="checkPoint-field">
          <span class="field-label">{{ 'scan.assetFound.descriptionLabel' | translate }}</span>
          <span class="field-value">{{ scanResult()!.description }}</span>
        </div>
        }

        <div class="checkPoint-field">
          <span class="field-label">{{ 'scan.assetFound.qrCodeLabel' | translate }}</span>
          <span class="field-value code">{{ scanResult()!.qr_code }}</span>
        </div>
      </div>

      <div class="flex gap-md mt-lg action-buttons">
        <app-button variant="outlined" (click)="scanAnother()" data-testid="scan-another-button">
          <app-icon name="camera" />
          {{ 'scan.scanAnother' | translate }}
        </app-button>

        <app-button (click)="recordInteraction()" data-testid="record-interaction-button">
          <app-icon name="clipboard-pen" />
          {{ 'scan.assetFound.recordInteraction' | translate }}
        </app-button>

        <app-button variant="outlined" (click)="viewCheckPoint()" data-testid="view-asset-button">
          <app-icon name="eye" />
          {{ 'scan.assetFound.viewDetails' | translate }}
        </app-button>
      </div>
    </div>
    }
  </app-card-content>
</app-card>